import IcAccount from 'assets/Icons/IcAccount';
import IcApt from 'assets/Icons/IcApt';
import Box from 'components/Box';
import Modal from 'components/Modal';
import Text from 'components/Text';
import userSelectors from 'features/user/services/selectors';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import IcTicket from 'assets/Icons/IcTicket';
import IcPhone from 'assets/Icons/IcPhone';
import MultiPayBtnV2 from 'assets/Icons/multiPayBtnV2';
import Tip from 'assets/Icons/tip';
import IcPayment from 'assets/Icons/IcPayment';
import IcConvert from 'assets/Icons/IcConvert';
import IcProtect from 'assets/Icons/IcProtect';
import IcNumber from 'assets/Icons/IcNumber';
import dayjs from 'dayjs';
import IcReview from 'assets/Icons/IcReview';
import { Rate } from 'antd';
import { formatCurrency } from 'utils/formatCurrency';
import ModalAdjustTipForStaff, { IModalAdjustTipForStaffRef } from 'features/cashier/components/ModalAdjustTipForStaff';
import { verifyAdminPW } from 'widgets/RequestAdminLocalPW/Callback';
import Button from 'components/Button';
import Split from 'assets/Icons/split';
import IcTickV3 from 'assets/Icons/IcTickV3';
import IcSend from 'assets/Icons/IcSend';
import storage from 'utils/sessionStorage';
import Close from 'assets/Icons/close';
import colorTheme from 'constants/color';

type LastServiceInfoModalProps = {};

type LastServiceInfoModalRef = {
  open: () => void;
};

export const useLastServiceInfoModalRef = () => useRef<LastServiceInfoModalRef>(null);

const LastServiceInfoModal = forwardRef<LastServiceInfoModalRef, LastServiceInfoModalProps>((_props, ref) => {
  const [visible, setVisible] = useState(false);
  const lastTicket = userSelectors.lastTicket();
  
  const modalAdjustTipForStaffRef = useRef<IModalAdjustTipForStaffRef>(null);
  const handleClose = () => {
    setVisible(false);
  };

  useImperativeHandle(ref, () => ({
    open: () => {
      setVisible(true);
    },
  }));

  const itemCreditCard = lastTicket?.isMultiplePayCard
    ? lastTicket?.paymentMultipleCard?.[0]
    : lastTicket?.payments?.[0];

  const handleClickOpenAdjustTip = () => {
    if (!lastTicket) return;
    verifyAdminPW(() => {
      modalAdjustTipForStaffRef.current?.openWithDetail(lastTicket as any);
    });
  };

  const handleSendReview = () => {
    if (!lastTicket) return;
    window.open(`https://customer.fozito.com/store/${storage.shop_id}/feedback/${lastTicket.billId}`, '_blank');
  };

  return (
    <StyledModal
      modalTitle='LAST SERVICE INFO'
      visible={visible}
      onClose={handleClose}
      v2
      width={900}
      noneBodyStyle
      destroyOnClose
      wrapClassName='history-check-in-modal'
      footer={null}
      showClose
      closeIconCustom={
        <CloseIcon>
          <Close fill='#FFF' />
        </CloseIcon>
      }
    >
      <Container>
        <ServiceItem
          icon={<IcTicket width={40} height={40} fill='#2D6772' />}
          title='Ticket Number'
          styledValue={
            <TicketNumber>
              <Text variant='H5' color='text_1'>
                #{lastTicket?.ticketNumber}
              </Text>
            </TicketNumber>
          }
        />
        <ServiceItem
          icon={<IcApt width={40} height={40} fill='#0C865D' />}
          title='Date & Time'
          value={dayjs(lastTicket?.endTime).format('MM-DD-YYYY HH:mm A') || '-'}
        />
        <ServiceItem
          icon={<IcAccount width={40} height={40} fill='#2563EB' />}
          title='Customer Name'
          value={lastTicket?.customerName || 'Unknown Customer'}
        />
        <ServiceItem
          icon={<IcPhone width={40} height={40} fill='#808836' />}
          title='Phone Number'
          value={lastTicket?.customerPhone || '-'}
        />
        <ServiceItem
          icon={<IcReview width={40} height={40} fill='#EBA33B' />}
          title='Rating'
          styledValue={
            lastTicket?.stars ? (
              <Box display='flex' alignItems='center' gap='2'>
                <Rate disabled value={4} allowHalf />
                <Text variant='H5' color='v2_yellow_5'>
                  (4)
                </Text>
              </Box>
            ) : (
              <Button
                ntype='ORANGE_V2'
                cardCashier
                height={3}
                iconElement={<IcSend fill='#FFF' />}
                onClick={handleSendReview}
              >
                Send Review
              </Button>
            )
          }
        />
        <ServiceItem
          icon={<MultiPayBtnV2 width={40} height={40} fill='#FF7D00' />}
          title='Payment Type'
          value={'Credit Card'}
        />
        <ServiceItem
          icon={<MultiPayBtnV2 width={40} height={40} fill='#FFF' />}
          title='Total Amount'
          value={formatCurrency(lastTicket?.total || 0)}
          bgColor='#FFF6F9'
          bgColorIcon='#D2464F'
          styledValue={
            <Text variant='H2' color='v2_primary_bg_6'>
              {formatCurrency(lastTicket?.total || 0)}
            </Text>
          }
        />
        {lastTicket?.tip ? (
          <ServiceItem
            icon={<Tip width={40} height={40} fill='#FFF' />}
            title='Tip Amount'
            bgColorIcon='#0C865D'
            bgColor='#EFFFFA'
            styledValue={
              <Box display='flex' alignItems='center' gap='3'>
                <Text variant='H2' color='v2_green_6'>
                  {formatCurrency(lastTicket?.tip || 0)}
                </Text>
                {lastTicket?.tip > 0 && lastTicket?.items?.length > 1 && (
                  <Button
                    onClick={handleClickOpenAdjustTip}
                    ntype='GREEN'
                    cardCashier
                    iconElement={<Split fill='#FFF' />}
                  >
                    Modify Tip
                  </Button>
                )}
              </Box>
            }
          />
        ) : null}
        <ServiceItem
          icon={<IcPayment width={40} height={40} fill='#4F1787' />}
          title='Payment Status'
          styledValue={
            <Box display='flex' alignItems='center' gap='3' px='3' py='2' borderRadius='4px' bgColor='v2_green_2'>
              <IcTickV3 fill='#0C865D' />
              <Text variant='H5' color='v2_green_6'>
                PAID
              </Text>
            </Box>
          }
        />
        <ServiceItem
          icon={<IcConvert width={40} height={40} fill='#C52884' />}
          title='Transaction Number'
          styledValue={
            <TransactionNumber>
              <Text variant='H5'>
                {itemCreditCard?.transNumber ? `#${itemCreditCard.transNumber}` : '-'}
              </Text>
            </TransactionNumber>
          }
        />
        <ServiceItem
          icon={<IcProtect width={40} height={40} fill='#2D6772' />}
          title='Authorization Number'
          value={itemCreditCard?.authCode || '-'}
        />
        <ServiceItem
          icon={<IcNumber width={40} height={40} fill='#0C865D' />}
          title='Last 4 Digits'
          value={itemCreditCard?.last4 || '-'}
        />
      </Container>
      <ModalAdjustTipForStaff ref={modalAdjustTipForStaffRef} />
    </StyledModal>
  );
});

LastServiceInfoModal.displayName = 'LastServiceInfoModal';
export default LastServiceInfoModal;

const StyledModal = styled(Modal)`
  .ant-modal-close:hover {
    background-color: transparent;
  }
`;

const CloseIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #fff;
  border-radius: 4px;
  padding: 2px;

  &:hover {
    opacity: 0.8;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 24px 32px 24px;
  font-family: 'Poppins';

  .text-medium {
    font-weight: 500;
  }
`;

const TicketNumber = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 12px;
  border-radius: 8px;
  background: #232f3e;
`;

const TransactionNumber = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 8px;
  background: yellow;
`;
interface ServiceItemProps {
  icon: React.ReactNode;
  title: string;
  value?: string | number;
  bgColorIcon?: string;
  styledValue?: React.ReactNode;
  color?: string;
  bgColor?: string;
}

const ServiceItem = ({ icon, title, value, bgColor, styledValue, color, bgColorIcon }: ServiceItemProps) => {
  return (
    <ServiceItemContainer style={{ background: bgColor || 'transparent' }}>
      <Box display='flex' alignItems='center' gap='3'>
        <Box
          className='box-icon'
          display='flex'
          justifyContent='center'
          alignItems='center'
          style={{ background: bgColorIcon || 'transparent' }}
        >
          {icon}
        </Box>
        <Text variant='H4' className='text-medium' color='text_4_v2'>
          {title}
        </Text>
      </Box>
      {styledValue ? (
        <>{styledValue}</>
      ) : (
        <Text variant='H5' style={{ color: color || '#1D2129' }}>
          {value}
        </Text>
      )}
    </ServiceItemContainer>
  );
};

const ServiceItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e9edf2;

  .box-icon {
    width: 48px;
    height: 48px;
    border-radius: 8px;
  }

  &:hover {
    cursor: pointer;
  }

  .ant-rate {
    font-size: 40px;
    line-height: 1;
  }

  .CONTENT_2 {
    font-size: 16px;
    font-family: 'Poppins';
  }
`;
